/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-empty-interface */
/* eslint-disable react/no-unstable-nested-components */
import IndicatorCategoryName from "components/financials/IndicatorCategoryName";
import getCategoryOrder from "components/financials/getCategoryOrder";
import { getSortedIndicatorCategories } from "components/financials/modern/data/hooks";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import IndicatorListItem from "components/financials/modern/indicators/IndicatorListItem";
import IndicatorSearchInput from "components/financials/modern/indicators/IndicatorSearchInput";
import { INDICATOR_SEARCH_QUERY_ATOM } from "components/financials/modern/indicators/atoms";
import getIndicatorDocumentation from "components/financials/modern/indicators/getIndicatorDocumentation";
import { INDICATORS_LIMIT } from "components/financials/modern/templates/form";
import classNames from "components/ui/classNames";
import { Reorder } from "framer-motion";
import { useAtom } from "jotai";
import usePagination from "lib/hooks/usePagination";
import type { WithoutTypename } from "lib/types/graphql";
import orderBy from "lodash/orderBy";
import { ChevronDownIcon, ChevronUpIcon, ChevronsUpIcon, GripVerticalIcon } from "lucide-react";
import { matchSorter } from "match-sorter";
import { startTransition, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { IIndicatorCategory } from "types/graphqlTypes";

interface IndicatorsListProps {
  indicators: WithoutTypename<IIndicatorFieldsFragment>[];
  selectedIndicators: WithoutTypename<IIndicatorFieldsFragment>[];
  onSelectIndicators: (indicators: WithoutTypename<IIndicatorFieldsFragment>[]) => void;
}
export default function IndicatorsList({ indicators, selectedIndicators, onSelectIndicators }: IndicatorsListProps) {
  const intl = useIntl();
  const [draggable, setDraggable] = useState(false);
  const [searchQuery, setSearchQuery] = useAtom(INDICATOR_SEARCH_QUERY_ATOM);
  const [filterBySelectedIndicators, setFilterBySelectedIndicators] = useState(selectedIndicators.length > 0);
  const [filterByCategories, setFilterByCategories] = useState<IIndicatorCategory[]>([]);
  const filteredIndicators = matchSorter(filterBySelectedIndicators ? selectedIndicators : indicators, searchQuery, {
    keys: ["documentation.*.name", "documentation.*.abbreviation"],
    keepDiacritics: true,
    sorter: (rankedItems) => {
      if (searchQuery) {
        return rankedItems.sort((a, b) => {
          return b.rank - a.rank;
        });
      }
      return orderBy(
        rankedItems.map((rankedItem) => ({
          item: rankedItem,
          categoryOrder: getCategoryOrder(rankedItem.item.category),
          name: getIndicatorDocumentation(intl, rankedItem.item).name,
        })),
        ["categoryOrder", "name"],
        ["asc", "asc"],
      ).map(({ item }) => item);
    },
  });
  const categories = getSortedIndicatorCategories(filteredIndicators);
  const pagination = usePagination({
    items: filteredIndicators.filter((candidate) => {
      if (filterByCategories.length === 0) {
        return true;
      }
      return filterByCategories.includes(candidate.category);
    }),
    itemsPerPage: 8,
    mode: "slice",
  });
  const indicatorsToDisplay = filterBySelectedIndicators
    ? selectedIndicators.filter((candidate) => {
        if (filterByCategories.length === 0) {
          return true;
        }
        return filterByCategories.includes(candidate.category);
      })
    : pagination.currentItems;
  const isReorderable =
    filterBySelectedIndicators &&
    indicatorsToDisplay.length === selectedIndicators.length &&
    selectedIndicators.length > 0;

  return (
    <div className="space-y-4">
      <IndicatorSearchInput
        resultsCount={filteredIndicators.length}
        noResultsText="Nada encontrado..."
        placeholder="Pesquise indicadores"
        onSearch={(searchQuery) => {
          startTransition(() => {
            setSearchQuery(searchQuery);
            pagination.setCurrentPage(1);
          });
        }}
      />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-has-selected={(selectedIndicators.length > 0 && !filterBySelectedIndicators) || undefined}
          data-active={filterBySelectedIndicators || undefined}
          onClick={() => {
            startTransition(() => {
              setFilterBySelectedIndicators(!filterBySelectedIndicators);
              setFilterByCategories([]);
            });
          }}
          className={classNames(
            "inline-flex items-center",
            "rounded-full border bg-white px-3 py-1 text-xs font-medium shadow-sm",
            "hover:opacity-80 disabled:opacity-60",
            "divide divide-x",
            "divide-slate-300 border-slate-300 bg-slate-50 text-slate-700",
            "data-[has-selected]:divide-orange-800/40 data-[has-selected]:border-orange-800/40 data-[has-selected]:bg-orange-50/50 data-[has-selected]:text-orange-700",
            "data-[active]:divide-teal-800/30 data-[active]:border-teal-800/30 data-[active]:bg-teal-50 data-[active]:text-teal-700 data-[active]:hover:opacity-80",
            "dark:divide-slate-700 dark:border-transparent dark:bg-slate-800 dark:text-slate-300",
            "dark:data-[active]:border-transparent dark:data-[active]:bg-teal-600/20 dark:data-[active]:text-teal-300 dark:data-[active]:hover:opacity-80",
          )}
        >
          <span className="pr-2 font-bold">
            <FormattedMessage
              defaultMessage="Selecionados"
              id="uEyQni"
              description="Filter by selected indicators in the template builder"
            />
          </span>
          <span className="pl-2">{selectedIndicators.length}</span>
        </button>

        {categories.map(({ category, totalCount }) => {
          const isFilteringByCategory = filterByCategories.includes(category);
          return (
            <button
              key={category}
              type="button"
              data-active={isFilteringByCategory || undefined}
              onClick={() => {
                if (filterByCategories.includes(category)) {
                  setFilterByCategories(filterByCategories.filter((candidate) => candidate !== category));
                  pagination.setCurrentPage(1);
                } else {
                  setFilterByCategories([category]);
                  pagination.setCurrentPage(1);
                }
              }}
              className={classNames(
                "inline-flex items-center",
                "rounded-full border bg-white px-3 py-1 text-xs font-medium shadow-sm",
                "divide divide-x",
                "border-slate-300 bg-slate-50 text-slate-700",
                "hover:opacity-70",
                "data-[active]:border-teal-800/30 data-[active]:bg-teal-50 data-[active]:text-teal-700 data-[active]:hover:opacity-80",
                "dark:divide-slate-700 dark:border-transparent dark:bg-slate-800 dark:text-slate-300",
                "dark:data-[active]:border-transparent dark:data-[active]:bg-teal-600/20 dark:data-[active]:text-teal-300 dark:data-[active]:hover:opacity-80",
              )}
            >
              <span className="pr-2 font-bold">
                <IndicatorCategoryName category={category} />
              </span>
              <span className="pl-2">{totalCount}</span>
            </button>
          );
        })}
      </div>

      <div className={classNames("flex flex-1 items-center space-x-2")}>
        <ScrollArrowButton
          disabled={!pagination.hasNextPage || filterBySelectedIndicators}
          onClick={() => pagination.goToNextPage()}
        >
          <ChevronDownIcon strokeWidth={2} />
        </ScrollArrowButton>
        <ScrollArrowButton
          disabled={!pagination.hasPreviousPage || filterBySelectedIndicators}
          onClick={() => pagination.goToPreviousPage()}
        >
          <ChevronUpIcon strokeWidth={2} />
        </ScrollArrowButton>
        <ScrollArrowButton
          disabled={!pagination.hasPreviousPage || filterBySelectedIndicators}
          onClick={() => pagination.setCurrentPage(1)}
        >
          <ChevronsUpIcon strokeWidth={2} />
        </ScrollArrowButton>
        <div className="h-full w-20 rounded-md px-4 py-1 text-center text-sm font-bold text-slate-600 dark:text-slate-300">
          {pagination.currentPage} / {pagination.pages.length}
        </div>
      </div>

      <Reorder.Group
        axis="y"
        values={indicatorsToDisplay}
        onReorder={onSelectIndicators}
        className="-mx-2 mt-4"
        initial={false}
        animate={false}
        layoutScroll={true}
      >
        {indicatorsToDisplay.map((indicator) => {
          const viewerCanSelect = selectedIndicators.length < INDICATORS_LIMIT;
          return (
            <Reorder.Item
              id={indicator.objectId}
              key={indicator.objectId}
              value={indicator}
              dragListener={draggable}
              onDragEnd={() => setDraggable(false)}
              className="bg-white py-1 first:pt-0 last:pb-0 dark:bg-transparent"
              animate={false}
              initial={false}
            >
              <div className="flex items-center">
                <IndicatorListItem
                  indicator={indicator}
                  viewerCanSelect={viewerCanSelect}
                  shouldHighlightBox={!filterBySelectedIndicators}
                  isSelected={selectedIndicators.some(({ objectId }) => objectId === indicator.objectId)}
                  onToggleSelected={(nextSelected) => {
                    if (nextSelected) {
                      const nextIndicators = [...selectedIndicators, indicator];
                      onSelectIndicators(nextIndicators);
                    } else {
                      onSelectIndicators(selectedIndicators.filter(({ objectId }) => objectId !== indicator.objectId));
                    }
                  }}
                />

                <div className="ml-4 pr-2">
                  <div
                    className={classNames(
                      "flex flex-col items-center justify-center overflow-hidden rounded-md border bg-slate-50",
                      "divide-y divide-slate-200",
                      "dark:divide-slate-900 dark:border-slate-900 dark:bg-slate-700",
                    )}
                  >
                    <button
                      type="button"
                      disabled={!isReorderable}
                      className={classNames(
                        "p-1 px-2",
                        "hover:bg-indigo-50",
                        "disabled:pointer-events-none disabled:opacity-40",
                        "group",
                        "focus:outline-none focus-visible:bg-indigo-100",
                      )}
                      aria-label="Mover para cima"
                      onClick={() => {
                        onSelectIndicators(
                          arrayMoveCopying(
                            selectedIndicators,
                            selectedIndicators.indexOf(indicator),
                            selectedIndicators.indexOf(indicator) - 1,
                          ),
                        );
                      }}
                      onKeyDown={(event) => {
                        // Check if arrow down key was pressed
                        if (event.key === "ArrowDown") {
                          // Prevent default behavior
                          event.preventDefault();
                          // Move position down
                          onSelectIndicators(
                            arrayMoveCopying(
                              selectedIndicators,
                              selectedIndicators.indexOf(indicator),
                              selectedIndicators.indexOf(indicator) + 1,
                            ),
                          );
                        }
                        if (event.key === "ArrowUp") {
                          // Prevent default behavior
                          event.preventDefault();
                          // Move position up
                          onSelectIndicators(
                            arrayMoveCopying(
                              selectedIndicators,
                              selectedIndicators.indexOf(indicator),
                              selectedIndicators.indexOf(indicator) - 1,
                            ),
                          );
                        }
                      }}
                    >
                      <ChevronUpIcon
                        className="h-4 w-4 stroke-slate-600 group-hover:stroke-indigo-600 dark:stroke-white"
                        strokeWidth={2.5}
                        absoluteStrokeWidth={true}
                      />
                    </button>
                    <button
                      type="button"
                      disabled={!isReorderable}
                      className={classNames(
                        "p-1 px-2",
                        "hover:bg-indigo-50",
                        "disabled:pointer-events-none disabled:opacity-40",
                        "group",
                        "focus:outline-none focus-visible:bg-indigo-100",
                      )}
                      aria-label="Mover para baixo"
                      onKeyDown={(event) => {
                        // Check if arrow down key was pressed
                        if (event.key === "ArrowDown") {
                          // Prevent default behavior
                          event.preventDefault();
                          // Move position down
                          onSelectIndicators(
                            arrayMoveCopying(
                              selectedIndicators,
                              selectedIndicators.indexOf(indicator),
                              selectedIndicators.indexOf(indicator) + 1,
                            ),
                          );
                        }
                        if (event.key === "ArrowUp") {
                          // Prevent default behavior
                          event.preventDefault();
                          // Move position up
                          onSelectIndicators(
                            arrayMoveCopying(
                              selectedIndicators,
                              selectedIndicators.indexOf(indicator),
                              selectedIndicators.indexOf(indicator) - 1,
                            ),
                          );
                        }
                      }}
                      onClick={() => {
                        // Move position down
                        onSelectIndicators(
                          arrayMoveCopying(
                            selectedIndicators,
                            selectedIndicators.indexOf(indicator),
                            selectedIndicators.indexOf(indicator) + 1,
                          ),
                        );
                      }}
                    >
                      <ChevronDownIcon
                        className="h-4 w-4 stroke-slate-600 group-hover:stroke-indigo-600 dark:stroke-white"
                        strokeWidth={2.5}
                        absoluteStrokeWidth={true}
                      />
                    </button>

                    <button
                      type="button"
                      disabled={!isReorderable}
                      className={classNames(
                        "p-1 px-2",
                        "hover:bg-indigo-50",
                        "disabled:pointer-events-none disabled:opacity-40",
                        "group",
                        "focus:outline-none focus-visible:bg-indigo-100",
                      )}
                      onMouseEnter={() => setDraggable(true)}
                      onMouseLeave={() => setDraggable(false)}
                      onTouchStart={() => setDraggable(true)}
                    >
                      <GripVerticalIcon className="h-4 w-4 stroke-slate-600 group-hover:stroke-indigo-600 dark:stroke-white" />
                    </button>
                  </div>
                </div>
              </div>
            </Reorder.Item>
          );
        })}
      </Reorder.Group>
    </div>
  );
}

function arrayMoveCopying<T>(nodes: T[], fromIndex: number, toIndex: number) {
  const result = [...nodes];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result;
}

function ScrollArrowButton({
  className,
  children,
  ...props
}: React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>) {
  return (
    <button
      {...props}
      type="button"
      className={classNames(
        "flex flex-1 justify-center rounded-md border border-slate-200 bg-slate-50 p-2",
        "dark:bg-slate-800/70 dark:text-slate-200",
        "disabled:cursor-not-allowed disabled:text-slate-300",
        "hover:bg-slate-100/80",
        "dark:border-transparent dark:hover:bg-slate-700 dark:hover:opacity-80",
        className,
      )}
    >
      {children}
    </button>
  );
}
